import React from "react";

import Icon from "./Icon";
import ZestComponent from "./ZestComponent";
import type { IconLibrary, PillColor } from "./types";

// React rebuild of the ZEST v2 Figma component set Zest::Pill

// A filled pill's border matches its background. Most semantic colour tokens
// resolve identically across utilities, so `bg-x`/`border-x` pair up — but the
// three status tokens do not: `border-success|danger|warning` resolve to the
// darker 900 shade while `bg-success|warning|danger` resolve to 500. Those
// three therefore reference the palette scale directly, which is symmetric:
// border-green-500 is the same #06C882 as bg-success.
const FILL_COLORS: Record<PillColor, string[]> = {
  orange: ["bg-brand", "text-negative", "border-brand"],
  black: ["bg-dark", "text-negative", "border-dark"],
  slate: ["bg-light", "text-primary", "border-light"],
  qc: ["bg-qc", "text-negative", "border-qc"],
  service: ["bg-service", "text-negative", "border-service"],
  green: ["bg-success", "text-negative", "border-green-500"],
  yellow: ["bg-warning", "text-negative", "border-yellow-500"],
  red: ["bg-danger", "text-negative", "border-red-500"],
  new_jersey: ["bg-office-blue-500", "text-negative", "border-office-blue-500"],
  philadelphia: ["bg-office-green-500", "text-negative", "border-office-green-500"],
  new_york: ["bg-office-chestnut-500", "text-negative", "border-office-chestnut-500"],
  maryland: ["bg-office-mustard-500", "text-negative", "border-office-mustard-500"],
};

const OUTLINE_COLORS: Partial<Record<PillColor, string[]>> = {
  slate: ["border-primary", "text-primary"],
  new_jersey: ["border-office-blue-500", "text-office-blue-500"],
  philadelphia: ["border-office-green-500", "text-office-green-500"],
  new_york: ["border-office-chestnut-500", "text-office-chestnut-500"],
  maryland: ["border-office-mustard-500", "text-office-mustard-500"],
};

const pillColorClasses = ({ color, outlined }: { color: PillColor; outlined: boolean }): string => {
  const map = outlined ? OUTLINE_COLORS : FILL_COLORS;

  return (map[color] ?? FILL_COLORS.slate).join(" ");
};

export interface PillProps {
  text: React.ReactNode;
  color?: PillColor;
  icon?: string | null;
  iconLibrary?: IconLibrary;
  outlined?: boolean;
  classes?: string;
  colorClasses?: string | null;
}

const Pill = ({
  color = "slate",
  icon = null,
  iconLibrary = "standard",
  outlined = false,
  classes = "",
  colorClasses = null,
  text,
}: PillProps): JSX.Element => {
  const colorClassNames = colorClasses ?? pillColorClasses({ color, outlined });

  return (
    <ZestComponent
      name="pill"
      className={`inline-flex items-center justify-center gap-1 rounded-full text-sm font-bold whitespace-nowrap align-middle px-2 py-1 border-2 ${colorClassNames} ${classes}`}
      variants={{ color, icon }}
    >
      {icon && (
        <span className="flex h-4 w-4 items-center justify-center">
          <Icon icon={icon} library={iconLibrary} />
        </span>
      )}
      {text}
    </ZestComponent>
  );
};

export default Pill;
